import type { ViewStyle } from 'react-native';
import {
  type AnimatedStyle,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';

import { SPRING } from '@/constants/animation';

interface UsePressAnimationOptions {
  /** Scale when pressed. Default: `0.97`. */
  pressedScale?: number;
  /** Spring config. Default: `SPRING.snappy`. */
  spring?: { damping: number; stiffness: number };
}

/**
 * Returns an animated press-down style and press handlers.
 * Wrap the target in an `Animated.View` with the style, and spread
 * the handlers onto the pressable.
 */
export function usePressAnimation({
  pressedScale = 0.97,
  spring = SPRING.snappy,
}: UsePressAnimationOptions = {}) {
  const scale = useSharedValue(1);

  const animatedStyle: AnimatedStyle<ViewStyle> = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const onPressIn = () => {
    scale.value = withSpring(pressedScale, spring);
  };

  const onPressOut = () => {
    scale.value = withSpring(1, spring);
  };

  return { animatedStyle, onPressIn, onPressOut };
}
